import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, generatePath } from "react-router-dom";
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  TablePagination, CircularProgress, Chip, Select, MenuItem, FormControl,
  InputLabel, TextField, Stack, Card, CardContent, useMediaQuery, useTheme,
} from "@mui/material";
import { getAdminProducts } from "../../../utility/httpActions/adminActions";
import { frontendRoutes } from "../../../utility/frontendRoutes";
import { shopCategories } from "../../../utility/constants";
import { useDebounce } from "../../../hooks/useDebounce";
import useListQueryState from "../../../hooks/useListQueryState";

const PRODUCT_STATUSES = ["DRAFT", "PUBLISHED", "ARCHIVED", "OUT_OF_STOCK", "SUSPENDED"];

const statusColor = {
  DRAFT: "default",
  PUBLISHED: "success",
  ARCHIVED: "default",
  OUT_OF_STOCK: "warning",
  SUSPENDED: "error",
};

const formatCurrency = (cents, currency) => {
  const amount = ((cents || 0) / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currency ? `${currency} ${amount}` : amount;
};

const ProductList = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchParams, updateParams] = useListQueryState();
  const page = Number(searchParams.get("page")) || 0;
  const statusFilter = searchParams.get("status") || "";
  const categoryFilter = searchParams.get("category") || "";
  const sort = searchParams.get("sort") || "newest";
  const [searchInput, setSearchInput] = useState(() => searchParams.get("search") || "");
  const search = useDebounce(searchInput, 300);
  const limit = 20;

  // See UserList.jsx for why this guards against firing on mount.
  useEffect(() => {
    if (search === (searchParams.get("search") || "")) return;
    updateParams({ search, page: undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-products", page, statusFilter, categoryFilter, search, sort],
    queryFn: () =>
      getAdminProducts({
        page: page + 1,
        limit,
        status: statusFilter || undefined,
        category: categoryFilter || undefined,
        search: search || undefined,
        sort,
      }),
  });

  const handleRowClick = (productId) =>
    navigate(generatePath(frontendRoutes.ADMIN_PRODUCT_DETAIL, { productId }));

  const products = data?.products || [];

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={2}>Products</Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={2} flexWrap="wrap" useFlexGap>
        <TextField
          size="small"
          label="Search product or shop name"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ minWidth: 240 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => updateParams({ status: e.target.value, page: undefined })}
          >
            <MenuItem value="">All</MenuItem>
            {PRODUCT_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>{s.replace(/_/g, " ")}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={categoryFilter}
            label="Category"
            onChange={(e) => updateParams({ category: e.target.value, page: undefined })}
          >
            <MenuItem value="">All</MenuItem>
            {shopCategories.map((c) => (
              <MenuItem key={c.value} value={c.value}>{c.icon} {c.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Sort</InputLabel>
          <Select value={sort} label="Sort" onChange={(e) => updateParams({ sort: e.target.value, page: undefined })}>
            <MenuItem value="newest">Newest</MenuItem>
            <MenuItem value="oldest">Oldest</MenuItem>
            <MenuItem value="price_desc">Price (high to low)</MenuItem>
            <MenuItem value="price_asc">Price (low to high)</MenuItem>
            <MenuItem value="stock_asc">Stock (low to high)</MenuItem>
            <MenuItem value="stock_desc">Stock (high to low)</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {isLoading ? (
        <CircularProgress />
      ) : products.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No products found.</Typography>
      ) : isMobile ? (
        <Stack spacing={2}>
          {products.map((p) => (
            <Card key={p._id} variant="outlined" onClick={() => handleRowClick(p._id)} sx={{ cursor: "pointer" }}>
              <CardContent>
                <Typography fontWeight={600}>{p.productName}</Typography>
                <Typography variant="body2">Shop: {p.shop?.shopName || "—"}</Typography>
                <Typography variant="body2">
                  Status: <Chip label={p.status} size="small" color={statusColor[p.status] || "default"} />
                </Typography>
                <Typography variant="body2">Stock: {p.stockQuantity ?? "Unlimited"}</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {formatCurrency(p.priceCents, p.currency)}
                  {p.pricingMode === "OFFERS" ? " (hidden)" : ""}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Product</TableCell>
              <TableCell>Shop</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Stock</TableCell>
              <TableCell>Sold</TableCell>
              <TableCell>Rating</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map((p) => (
              <TableRow key={p._id} hover onClick={() => handleRowClick(p._id)} sx={{ cursor: "pointer" }}>
                <TableCell>{p.productName}</TableCell>
                <TableCell>{p.shop?.shopName || "—"}</TableCell>
                <TableCell>
                  <Chip label={p.status} size="small" color={statusColor[p.status] || "default"} />
                </TableCell>
                <TableCell>
                  {formatCurrency(p.priceCents, p.currency)}
                  {/* Hidden price: buyers don't see it and must make an offer. */}
                  {p.pricingMode === "OFFERS" ? " (hidden)" : ""}
                </TableCell>
                <TableCell>{p.stockQuantity ?? "Unlimited"}</TableCell>
                <TableCell>{p.numSold || 0}</TableCell>
                <TableCell>{p.rating ? `${p.rating.toFixed(1)} (${p.numReviews || 0})` : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <TablePagination
        component="div"
        count={data?.total || 0}
        page={page}
        onPageChange={(_, p) => updateParams({ page: p || undefined })}
        rowsPerPage={limit}
        rowsPerPageOptions={[limit]}
      />
    </Box>
  );
};

export default ProductList;
